import React from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { Plus, Edit2, Trash2, X, Search } from "lucide-react";

// ── Product picker ──────────────────────────────────────────────
function ProductPicker({ selected, onChange }) {
    const [q, setQ] = React.useState("");
    const [results, setResults] = React.useState([]);
    const [allProds, setAllProds] = React.useState([]);

    // Load names for already-selected IDs on mount
    React.useEffect(() => {
        if (!selected?.length) return;
        api.get("/admin/products").then(r => setAllProds(r.data || [])).catch(() => {});
    }, []);

    React.useEffect(() => {
        if (q.length < 2) { setResults([]); return; }
        const t = setTimeout(async () => {
            try {
                const r = await api.get(`/admin/products?q=${encodeURIComponent(q)}&limit=12`);
                setResults((r.data || []).filter(p => !selected.includes(p.product_id)));
            } catch {}
        }, 250);
        return () => clearTimeout(t);
    }, [q, selected]);

    const add = (p) => { onChange([...selected, p.product_id]); setAllProds(prev => [...prev.filter(x => x.product_id !== p.product_id), p]); setQ(""); setResults([]); };
    const remove = (id) => onChange(selected.filter(x => x !== id));
    const nameOf = (id) => allProds.find(p => p.product_id === id)?.name || id;

    return (
        <div className="space-y-2">
            <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                <input value={q} onChange={e => setQ(e.target.value)} placeholder="Caută produs după nume sau cod..."
                    className="w-full pl-8 pr-3 py-2 border border-foreground bg-transparent text-sm" />
                {results.length > 0 && (
                    <div className="absolute z-10 left-0 right-0 top-full mt-1 bg-card border border-border shadow-lg max-h-48 overflow-y-auto">
                        {results.map(p => (
                            <button key={p.product_id} type="button" onClick={() => add(p)}
                                className="w-full text-left px-3 py-2 text-sm hover:bg-muted flex items-center gap-2 border-b border-border/50 last:border-0">
                                <span className="font-medium flex-1 truncate">{p.name}</span>
                                <span className="text-xs text-muted-foreground font-mono shrink-0">{p.code || ""}</span>
                            </button>
                        ))}
                    </div>
                )}
            </div>
            {selected.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                    {selected.map(id => (
                        <span key={id} className="inline-flex items-center gap-1 bg-primary/10 text-primary text-xs px-2 py-1 rounded-full">
                            <span className="max-w-[200px] truncate">{nameOf(id)}</span>
                            <button type="button" onClick={() => remove(id)} className="hover:text-destructive ml-0.5"><X className="h-3 w-3" /></button>
                        </span>
                    ))}
                </div>
            )}
            {selected.length === 0 && <p className="text-xs text-muted-foreground">Niciun produs selectat — discountul se aplică tuturor produselor.</p>}
        </div>
    );
}

// ── Category picker ─────────────────────────────────────────────
function CategoryPicker({ selected, onChange }) {
    const [cats, setCats] = React.useState([]);
    React.useEffect(() => {
        api.get("/admin/categories").then(r => setCats(r.data || [])).catch(() => {});
    }, []);

    const toggle = (id) => onChange(selected.includes(id) ? selected.filter(x => x !== id) : [...selected, id]);
    const nameOf = (id) => cats.find(c => c.category_id === id)?.name || id;
    const topCats = cats.filter(c => !c.parent_id).sort((a, b) => (a.order || 0) - (b.order || 0));
    const childrenOf = (id) => cats.filter(c => c.parent_id === id);

    return (
        <div className="space-y-2">
            <div className="border border-border rounded-lg max-h-52 overflow-y-auto divide-y divide-border/50">
                {topCats.map(cat => (
                    <React.Fragment key={cat.category_id}>
                        <label className="flex items-center gap-2 px-3 py-2 hover:bg-muted cursor-pointer text-sm font-medium">
                            <input type="checkbox" checked={selected.includes(cat.category_id)} onChange={() => toggle(cat.category_id)} />
                            {cat.name}
                        </label>
                        {childrenOf(cat.category_id).map(child => (
                            <label key={child.category_id} className="flex items-center gap-2 pl-7 pr-3 py-1.5 hover:bg-muted cursor-pointer text-sm text-muted-foreground">
                                <input type="checkbox" checked={selected.includes(child.category_id)} onChange={() => toggle(child.category_id)} />
                                ↳ {child.name}
                            </label>
                        ))}
                    </React.Fragment>
                ))}
            </div>
            {selected.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                    {selected.map(id => (
                        <span key={id} className="inline-flex items-center gap-1 bg-primary/10 text-primary text-xs px-2 py-1 rounded-full">
                            {nameOf(id)}
                            <button type="button" onClick={() => toggle(id)} className="hover:text-destructive ml-0.5"><X className="h-3 w-3" /></button>
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
}

// ── Main component ───────────────────────────────────────────────
const EMPTY = { code: "", discount_type: "percent", value: 10, applies_to: "cart", target_ids: [], min_cart_value: 0, max_uses: 0, stackable: false, active: true, valid_from: "", valid_to: "" };

export default function AdminDiscounts() {
    const [items, setItems] = React.useState([]);
    const [editing, setEditing] = React.useState(null);

    const load = React.useCallback(() => api.get("/admin/discounts").then(r => setItems(r.data || [])), []);
    React.useEffect(() => { load(); }, [load]);

    const save = async () => {
        if (!editing.code) return toast.error("Codul este obligatoriu");
        try {
            if (editing.discount_id) await api.put(`/admin/discounts/${editing.discount_id}`, editing);
            else await api.post("/admin/discounts", editing);
            toast.success("Salvat"); setEditing(null); load();
        } catch (e) { toast.error(e.response?.data?.detail || "Eroare"); }
    };
    const del = async (id) => {
        if (!window.confirm("Ștergi discountul?")) return;
        await api.delete(`/admin/discounts/${id}`);
        toast.success("Șters"); load();
    };

    const set = (k, v) => setEditing(prev => ({ ...prev, [k]: v }));

    return (
        <div data-testid="admin-discounts">
            <div className="flex items-center justify-between mb-6">
                <div><div className="overline mb-1">Administrare</div><h1 className="font-heading text-3xl uppercase">Coduri discount</h1></div>
                <button onClick={() => setEditing({ ...EMPTY })} className="btn-primary flex items-center gap-2">
                    <Plus className="h-4 w-4" /> Adaugă
                </button>
            </div>

            <div className="border-2 border-foreground overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="bg-foreground text-background">
                        <tr>{["Cod", "Tip", "Valoare", "Aplicat la", "Ținte", "Cumulabil", "Activ", "Folosit", ""].map(h =>
                            <th key={h} className="text-left px-3 py-2 font-mono text-xs uppercase">{h}</th>
                        )}</tr>
                    </thead>
                    <tbody>
                        {items.length === 0 && <tr><td colSpan={9} className="px-3 py-8 text-center text-muted-foreground font-mono">Nu există discount-uri</td></tr>}
                        {items.map((it, i) => (
                            <tr key={it.discount_id || i} className="border-t border-border hover:bg-muted">
                                <td className="px-3 py-2 font-mono font-bold">{it.code}</td>
                                <td className="px-3 py-2">{it.discount_type}</td>
                                <td className="px-3 py-2">{it.value}{it.discount_type === "percent" ? "%" : " RON"}</td>
                                <td className="px-3 py-2">{it.applies_to}</td>
                                <td className="px-3 py-2 text-xs text-muted-foreground">{(it.target_ids || []).length ? `${it.target_ids.length} selectat(e)` : "—"}</td>
                                <td className="px-3 py-2">{it.stackable ? "✓" : "—"}</td>
                                <td className="px-3 py-2">{it.active ? "✓" : "—"}</td>
                                <td className="px-3 py-2">{it.used_count || 0}</td>
                                <td className="px-3 py-2 text-right whitespace-nowrap">
                                    <button onClick={() => setEditing(JSON.parse(JSON.stringify(it)))} className="btn-ghost"><Edit2 className="h-4 w-4" /></button>
                                    <button onClick={() => del(it.discount_id)} className="btn-ghost text-destructive"><Trash2 className="h-4 w-4" /></button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {editing && (
                <div className="fixed inset-0 z-50 bg-foreground/50 flex items-center justify-center p-4">
                    <div className="bg-card border-2 border-foreground max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between p-4 border-b-2 border-foreground">
                            <h2 className="font-heading text-xl uppercase">{editing.discount_id ? "Editează discount" : "Discount nou"}</h2>
                            <button onClick={() => setEditing(null)}><X className="h-5 w-5" /></button>
                        </div>
                        <div className="p-5 grid grid-cols-2 gap-4">

                            {/* Cod */}
                            <div>
                                <label className="block font-mono text-[11px] uppercase tracking-wide text-muted-foreground mb-1">Cod *</label>
                                <input value={editing.code} onChange={e => set("code", e.target.value.toUpperCase())}
                                    className="w-full px-3 py-2 border border-foreground bg-transparent text-sm font-mono uppercase" placeholder="ex: VARA20" />
                            </div>

                            {/* Tip */}
                            <div>
                                <label className="block font-mono text-[11px] uppercase tracking-wide text-muted-foreground mb-1">Tip</label>
                                <select value={editing.discount_type} onChange={e => set("discount_type", e.target.value)}
                                    className="w-full px-3 py-2 border border-foreground bg-transparent text-sm">
                                    <option value="percent">Procentual (%)</option>
                                    <option value="fixed">Fix (RON)</option>
                                </select>
                            </div>

                            {/* Valoare */}
                            <div>
                                <label className="block font-mono text-[11px] uppercase tracking-wide text-muted-foreground mb-1">
                                    Valoare {editing.discount_type === "percent" ? "(%)" : "(RON)"}
                                </label>
                                <input type="number" step="0.01" min="0" value={editing.value ?? ""}
                                    onChange={e => set("value", Number(e.target.value))}
                                    className="w-full px-3 py-2 border border-foreground bg-transparent text-sm" />
                            </div>

                            {/* Min coș */}
                            <div>
                                <label className="block font-mono text-[11px] uppercase tracking-wide text-muted-foreground mb-1">Min valoare coș (RON)</label>
                                <input type="number" step="0.01" min="0" value={editing.min_cart_value ?? ""}
                                    onChange={e => set("min_cart_value", Number(e.target.value))}
                                    className="w-full px-3 py-2 border border-foreground bg-transparent text-sm" />
                            </div>

                            {/* Aplicat la */}
                            <div>
                                <label className="block font-mono text-[11px] uppercase tracking-wide text-muted-foreground mb-1">Aplicat la</label>
                                <select value={editing.applies_to} onChange={e => set("applies_to", e.target.value)}
                                    className="w-full px-3 py-2 border border-foreground bg-transparent text-sm">
                                    <option value="cart">Coș întreg</option>
                                    <option value="product">Produse specifice</option>
                                    <option value="category">Categorii specifice</option>
                                </select>
                            </div>

                            {/* Max folosiri */}
                            <div>
                                <label className="block font-mono text-[11px] uppercase tracking-wide text-muted-foreground mb-1">Max folosiri (0 = nelimitat)</label>
                                <input type="number" min="0" value={editing.max_uses ?? ""}
                                    onChange={e => set("max_uses", Number(e.target.value))}
                                    className="w-full px-3 py-2 border border-foreground bg-transparent text-sm" />
                            </div>

                            {/* Valabil de la / până la */}
                            <div>
                                <label className="block font-mono text-[11px] uppercase tracking-wide text-muted-foreground mb-1">Valabil de la</label>
                                <input type="datetime-local" value={(editing.valid_from || "").slice(0, 16)}
                                    onChange={e => set("valid_from", e.target.value)}
                                    className="w-full px-3 py-2 border border-foreground bg-transparent text-sm" />
                            </div>
                            <div>
                                <label className="block font-mono text-[11px] uppercase tracking-wide text-muted-foreground mb-1">Valabil până la</label>
                                <input type="datetime-local" value={(editing.valid_to || "").slice(0, 16)}
                                    onChange={e => set("valid_to", e.target.value)}
                                    className="w-full px-3 py-2 border border-foreground bg-transparent text-sm" />
                            </div>

                            {/* Checkboxes */}
                            <div className="col-span-2 flex gap-6">
                                <label className="flex items-center gap-2 text-sm cursor-pointer">
                                    <input type="checkbox" checked={!!editing.stackable} onChange={e => set("stackable", e.target.checked)} />
                                    Cumulabil cu alte promoții
                                </label>
                                <label className="flex items-center gap-2 text-sm cursor-pointer">
                                    <input type="checkbox" checked={!!editing.active} onChange={e => set("active", e.target.checked)} />
                                    Activ
                                </label>
                            </div>

                            {/* Target IDs — selector vizual */}
                            {editing.applies_to !== "cart" && (
                                <div className="col-span-2">
                                    <label className="block font-mono text-[11px] uppercase tracking-wide text-muted-foreground mb-2">
                                        {editing.applies_to === "product" ? "Produse țintă" : "Categorii țintă"}
                                    </label>
                                    {editing.applies_to === "product" ? (
                                        <ProductPicker
                                            selected={editing.target_ids || []}
                                            onChange={ids => set("target_ids", ids)}
                                        />
                                    ) : (
                                        <CategoryPicker
                                            selected={editing.target_ids || []}
                                            onChange={ids => set("target_ids", ids)}
                                        />
                                    )}
                                </div>
                            )}

                        </div>
                        <div className="p-4 border-t-2 border-foreground flex justify-end gap-2">
                            <button onClick={() => setEditing(null)} className="btn-outline">Anulează</button>
                            <button onClick={save} className="btn-primary">Salvează</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
